/**
 * Verify Page Component
 * 
 * Handles email verification by extracting token from URL and making API call.
 * Displays appropriate success or error message.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import MessagePage from './MessagePage';

/**
 * Verify Page component
 */
const VerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [verificationResult, setVerificationResult] = useState<{
    success: boolean;
    title: string;
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const hasVerified = useRef<boolean>(false);

  useEffect(() => {
    const verifyToken = async () => {
      // Prevent multiple calls using ref
      if (hasVerified.current) {
        return;
      }
      
      hasVerified.current = true;
      
      const token = searchParams.get('token');

      if (!token) {
        setVerificationResult({
          success: false,
          title: 'Verification Failed',
          message: 'No verification token provided. Please check your email link and try again.',
          type: 'error',
        });
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Make POST request to verify endpoint with token
        const response = await apiService.post('/auth/verify', {
          token: token,
        });

        // Success response
        setVerificationResult({
          success: true,
          title: 'Email Verified Successfully',
          message: response.data?.message || 'Your email has been verified. You can now close this page and log in to your account.',
          type: 'success',
        });

      } catch (error: any) {
        // Handle different error scenarios
        let errorMessage = 'Verification failed. Please try again or contact support.';
        
        if (error.response?.status === 400) {
          errorMessage = 'Invalid or expired verification token. Please request a new verification email.';
        } else if (error.response?.status === 404) {
          errorMessage = 'Verification token not found. Please check your email link.';
        } else if (error.response?.status === 409) {
          errorMessage = 'This email has already been verified. You can log in to your account.';
        } else if (error.message) {
          errorMessage = error.message;
        }

        setVerificationResult({
          success: false,
          title: 'Verification Failed',
          message: errorMessage,
          type: 'error',
        });

      } finally {
        setIsLoading(false);
      }
    };

    // Call verification function
    verifyToken();
  }, []); // Empty dependency array to run only once

  // Show loading state
  if (isLoading) {
    return (
      <MessagePage
        title="Verifying Email"
        message="Please wait while we verify your email address..."
        type="info"
        showCloseButton={false}
      />
    );
  }

  // Show verification result
  if (verificationResult) {
    return (
      <MessagePage
        title={verificationResult.title}
        message={verificationResult.message}
        type={verificationResult.type}
        showCloseButton={true}
        autoClose={verificationResult.success ? 10 : 10} // Auto close success after 10 seconds
      />
    );
  }

  // Fallback (should not reach here)
  return (
    <MessagePage
      title="Verification Error"
      message="An unexpected error occurred during verification."
      type="error"
      showCloseButton={true}
    />
  );
};

export default VerifyPage;
